import DoneIcon from '@mui/icons-material/Done';
import { Button, Grid } from '@mui/material';
import classNames from 'classnames';
import ProductMakeOfferModal from 'components/ProductMakeOfferModal/ProductMakeOfferModal';
import { BUY_PRODUCT, MY } from 'config/fe-routes';
import useUser from 'hooks/useUser';
import { useRouter } from 'next/router';
import ShieldIcon from 'public/icons/icn-shield-bold.svg';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import OfferLoginService from 'services/offer-login.service';
import { buyButtonClicked, makeOfferButtonClicked } from 'store/actions/product-details.actions';
import { useAppDispatch } from 'store/hooks';
import { OfferStatus, OfferStatusOption } from 'types/offer.types';
import { ProductStatus, ProductType } from 'types/product.types';
import { redirectTo } from 'utils/route-helpers';

const offerLoginService = new OfferLoginService();

interface ProductActionsProps {
  product: ProductType;
  isOwner: boolean;
  buyerProtectionHighlighted: boolean;
  offerEnabled: boolean;
  refreshParentDataFn?: (ignoreLoader?: boolean) => void;
}

export const ProductActions = ({
  product,
  isOwner,
  buyerProtectionHighlighted,
  offerEnabled,
  refreshParentDataFn,
}: ProductActionsProps): JSX.Element | null => {
  const { t } = useTranslation();
  const { query } = useRouter();
  const { user } = useUser();
  const dispatch = useAppDispatch();
  const [isMakeOfferOpen, setMakeOfferOpen] = useState(false);
  const [restoredOfferPrice, setRestoredOfferPrice] = useState('');

  const shopSlug = query['shop-slug']?.toString();
  const productSlug = query['product-slug']?.toString();
  const sourceModule = query['source_module']?.toString();

  // Re-open the offer modal (with the typed price) after a guest logs in mid-offer
  useEffect(() => {
    if (!user?.isLoggedIn) {
      return;
    }

    const offerData = offerLoginService.getOfferData();

    if (offerData && offerData.productId === product?.id) {
      offerLoginService.clearOfferData();
      setRestoredOfferPrice(offerData.offeredPrice ?? '');
      setMakeOfferOpen(true);
    }
  }, [user?.isLoggedIn, product?.id]);

  // Don't show action buttons for owner, sold, hidden, or not-visible products
  if (
    isOwner ||
    product?.status === ProductStatus.SOLD ||
    product?.status === ProductStatus.HIDDEN ||
    product?.status === ProductStatus.NOT_VISIBLE ||
    product?.shop?.isBlocked
  ) {
    return null;
  }

  const clickBuy = () => {
    dispatch(buyButtonClicked(product));
    redirectTo({
      ...BUY_PRODUCT(shopSlug, productSlug),
      // checkout entry attribution (DEV-2119) — the buy page reads this into entry_source.
      // Kebab-case to match the mobile app's route param, the cross-platform convention.
      query: {
        'entry-source': 'product_page',
        ...(sourceModule && { source_module: sourceModule }),
      },
    });
  };

  const clickMakeAnOffer = () => {
    dispatch(makeOfferButtonClicked(product));

    if (([OfferStatusOption.PENDING, OfferStatusOption.ACCEPTED] as OfferStatus[]).includes(product?.offer?.status as OfferStatus)) {
      redirectTo(MY.CONVERSATION(shopSlug));
    } else {
      setMakeOfferOpen(true);
    }
  };

  return (
    <>
      <ProductMakeOfferModal
        product={product}
        open={isMakeOfferOpen}
        onClose={() => {
          setMakeOfferOpen(false);
          setRestoredOfferPrice('');
        }}
        initialOfferPrice={restoredOfferPrice}
        onOfferCreated={() => refreshParentDataFn?.(true)}
      />

      {/* BUY BUTTON */}
      <Grid item xs={12}>
        <Button
          className="buy-button"
          variant="contained"
          color="primary"
          fullWidth={true}
          onClick={clickBuy}
          disabled={product?.shop?.holidayMode}
        >
          {product?.shop?.holidayMode ? (
            <>
              <img src="/icons/icn-palm-tree.svg" className="palm-icon" alt="Palm tree icon" />
              {t('pages.account.shop-on-holiday')}
            </>
          ) : buyerProtectionHighlighted ? (
            <>
              <ShieldIcon className="mr-1" stroke="#FFFFFF" />
              {t('components.page-product.product-details.shield.buyer-protection-highlighted.buy-btn')}
            </>
          ) : (
            t('common.buy')
          )}
        </Button>
      </Grid>

      {/* MAKE AN OFFER BUTTON */}
      {!product?.shop?.holidayMode && +product?.price > 0 && offerEnabled && (
        <Grid item xs={12} className="mt-2">
          <Button
            className={classNames('make-offer-button', product?.offer?.status)}
            variant="outlined"
            color="primary"
            fullWidth={true}
            onClick={() => clickMakeAnOffer()}
            startIcon={product?.offer?.status === OfferStatusOption.ACCEPTED ? <DoneIcon /> : null}
          >
            {{
              [OfferStatusOption.ACCEPTED]: t('common.offer-accepted'),
              [OfferStatusOption.PENDING]: t('common.offer-pending'),
            }[product?.offer?.status] ?? t('common.make-offer')}
          </Button>
        </Grid>
      )}
    </>
  );
};

export default ProductActions;
